import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
const sections = [{
  id: 'acceptance',
  title: '1. Acceptance of Terms'
}, {
  id: 'about',
  title: '2. About Aijentik'
}, {
  id: 'use-of-services',
  title: '3. Use of Services'
}, {
  id: 'ai-content',
  title: '4. AI-Generated Content Disclaimer'
}, {
  id: 'user-data',
  title: '5. User Data and Inputs'
}, {
  id: 'intellectual-property',
  title: '6. Intellectual Property'
}, {
  id: 'third-party',
  title: '7. Third-Party Services'
}, {
  id: 'liability',
  title: '8. Limitation of Liability'
}, {
  id: 'availability',
  title: '9. Availability of Services'
}, {
  id: 'termination',
  title: '10. Termination'
}, {
  id: 'indemnity',
  title: '11. Indemnity'
}, {
  id: 'privacy',
  title: '12. Privacy'
}, {
  id: 'changes',
  title: '13. Changes to Terms'
}, {
  id: 'governing-law',
  title: '14. Governing Law'
}, {
  id: 'contact',
  title: '15. Contact'
}];
export function TermsAndConditions() {
  const [activeSection, setActiveSection] = useState(sections[0].id);
  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => {
      const sectionElements = sections.map(s => document.getElementById(s.id));
      const scrollPosition = window.scrollY + 200;
      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const section = sectionElements[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 120;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  return <main className="relative z-10 pt-32 pb-24 min-h-screen" data-id="element-1292">
      <div className="max-w-7xl mx-auto px-6" data-id="element-1293">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="max-w-3xl mb-16" data-id="element-1294">
          <h1 className="text-4xl md:text-5xl font-display mb-6" data-id="element-1295">
            Terms & Conditions
          </h1>
          <p className="text-xl text-gray-400 font-medium mb-4" data-id="element-1296">
            The rules, responsibilities, and legal terms governing your use of
            Aijentik
          </p>
          <p className="text-sm text-brand-orange" data-id="element-1297">Last Updated: April 2026</p>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-16 relative" data-id="element-1298">
          {/* Main Content */}
          <div className="flex-1 max-w-3xl" data-id="element-1299">
            <div className="prose prose-invert prose-gray max-w-none" data-id="element-1300">
              {/* Section 1 */}
              <motion.section id="acceptance" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1301">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1302">
                  1. Acceptance of Terms
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1303">
                  <p data-id="element-1304">
                    By accessing or using aijentik.com ("Website") and any
                    associated services ("Services"), you agree to be bound by
                    these Terms & Conditions ("Terms"). If you do not agree, you
                    must not use the Website or Services.
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1305" />

              {/* Section 2 */}
              <motion.section id="about" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1306">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1307">
                  2. About Aijentik
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1308">
                  <p data-id="element-1309">
                    Aijentik provides AI-powered tools, insights, and digital
                    services designed to analyse, automate, and optimise
                    business processes.
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1310" />

              {/* Section 3 */}
              <motion.section id="use-of-services" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1311">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1312">
                  3. Use of Services
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1313">
                  <p data-id="element-1314">
                    You agree to use the Website and Services only for lawful
                    purposes. You must not:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 marker:text-brand-orange" data-id="element-1315">
                    <li data-id="element-1316">
                      Use the Services in any way that breaches applicable laws
                      or regulations
                    </li>
                    <li data-id="element-1317">
                      Attempt to gain unauthorised access to systems or data
                    </li>
                    <li data-id="element-1318">
                      Interfere with the performance or security of the platform
                    </li>
                    <li data-id="element-1319">
                      Use the platform to generate harmful, misleading, or
                      unlawful content
                    </li>
                  </ul>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1320" />

              {/* Section 4 */}
              <motion.section id="ai-content" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1321">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1322">
                  4. AI-Generated Content Disclaimer
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1323">
                  <p data-id="element-1324">
                    Aijentik provides AI-generated outputs, insights, and
                    recommendations.
                  </p>
                  <p data-id="element-1325">You acknowledge that:</p>
                  <ul className="list-disc pl-5 space-y-2 marker:text-brand-orange" data-id="element-1326">
                    <li data-id="element-1327">
                      Outputs are generated automatically and may not be
                      accurate, complete, or reliable
                    </li>
                    <li data-id="element-1328">
                      Outputs are for informational purposes only and do not
                      constitute professional advice
                    </li>
                    <li data-id="element-1329">
                      You are solely responsible for verifying and acting on any
                      outputs
                    </li>
                  </ul>
                  <p className="font-medium text-white mt-4" data-id="element-1330">
                    Aijentik accepts no liability for decisions made based on
                    AI-generated content.
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1331" />

              {/* Section 5 */}
              <motion.section id="user-data" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1332">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1333">
                  5. User Data and Inputs
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1334">
                  <p data-id="element-1335">
                    You retain ownership of any data you submit to the platform.
                  </p>
                  <p data-id="element-1336">
                    By using the Services, you grant Aijentik a limited licence
                    to:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 marker:text-brand-orange" data-id="element-1337">
                    <li data-id="element-1338">Process your data to deliver the Services</li>
                    <li data-id="element-1339">Improve system performance and outputs</li>
                  </ul>
                  <p data-id="element-1340">
                    You must ensure you have the right to submit any data
                    provided.
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1341" />

              {/* Section 6 */}
              <motion.section id="intellectual-property" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1342">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1343">
                  6. Intellectual Property
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1344">
                  <p data-id="element-1345">
                    All content on the Website, including branding, software,
                    design, and materials, is owned by or licensed to Aijentik.
                  </p>
                  <p data-id="element-1346">You must not:</p>
                  <ul className="list-disc pl-5 space-y-2 marker:text-brand-orange" data-id="element-1347">
                    <li data-id="element-1348">
                      Copy, reproduce, or distribute content without permission
                    </li>
                    <li data-id="element-1349">Reverse engineer or attempt to extract source code</li>
                    <li data-id="element-1350">Use Aijentik branding without approval</li>
                  </ul>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1351" />

              {/* Section 7 */}
              <motion.section id="third-party" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1352">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1353">
                  7. Third-Party Services
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1354">
                  <p data-id="element-1355">
                    The platform may integrate with or rely on third-party
                    services.
                  </p>
                  <p data-id="element-1356">Aijentik is not responsible for:</p>
                  <ul className="list-disc pl-5 space-y-2 marker:text-brand-orange" data-id="element-1357">
                    <li data-id="element-1358">Third-party systems or outages</li>
                    <li data-id="element-1359">Data handling by third parties</li>
                    <li data-id="element-1360">Any loss arising from third-party services</li>
                  </ul>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1361" />

              {/* Section 8 */}
              <motion.section id="liability" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1362">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1363">
                  8. Limitation of Liability
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1364">
                  <p data-id="element-1365">To the maximum extent permitted by law:</p>
                  <ul className="list-disc pl-5 space-y-2 marker:text-brand-orange" data-id="element-1366">
                    <li data-id="element-1367">
                      Aijentik excludes all liability for indirect, incidental,
                      or consequential loss
                    </li>
                    <li data-id="element-1368">
                      Aijentik's total liability is limited to the amount paid
                      (if any) for Services
                    </li>
                  </ul>
                  <p className="text-sm text-gray-400 mt-4 italic" data-id="element-1369">
                    Nothing in these Terms excludes rights under the Australian
                    Consumer Law.
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1370" />

              {/* Section 9 */}
              <motion.section id="availability" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1371">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1372">
                  9. Availability of Services
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1373">
                  <p data-id="element-1374">
                    We do not guarantee uninterrupted or error-free access to
                    the Website or Services.
                  </p>
                  <p data-id="element-1375">
                    We may modify, suspend, or discontinue services at any time
                    without notice.
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1376" />

              {/* Section 10 */}
              <motion.section id="termination" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1377">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1378">
                  10. Termination
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1379">
                  <p data-id="element-1380">
                    We may suspend or terminate access to the Services at our
                    discretion if these Terms are breached.
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1381" />

              {/* Section 11 */}
              <motion.section id="indemnity" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1382">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1383">
                  11. Indemnity
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1384">
                  <p data-id="element-1385">
                    You agree to indemnify Aijentik against any claims, damages,
                    or losses arising from:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 marker:text-brand-orange" data-id="element-1386">
                    <li data-id="element-1387">Your misuse of the Services</li>
                    <li data-id="element-1388">Your breach of these Terms</li>
                    <li data-id="element-1389">Any data or content you submit</li>
                  </ul>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1390" />

              {/* Section 12 */}
              <motion.section id="privacy" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1391">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1392">
                  12. Privacy
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1393">
                  <p data-id="element-1394">
                    Your use of the Website is also governed by our Privacy
                    Policy.
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1395" />

              {/* Section 13 */}
              <motion.section id="changes" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1396">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1397">
                  13. Changes to Terms
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1398">
                  <p data-id="element-1399">
                    We may update these Terms at any time. Continued use of the
                    Website constitutes acceptance of the updated Terms.
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1400" />

              {/* Section 14 */}
              <motion.section id="governing-law" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1401">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1402">
                  14. Governing Law
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1403">
                  <p data-id="element-1404">
                    These Terms are governed by the laws of Queensland,
                    Australia.
                  </p>
                </div>
              </motion.section>

              <div className="h-px w-full bg-white/5 my-12" data-id="element-1405" />

              {/* Section 15 */}
              <motion.section id="contact" className="mb-16 scroll-mt-32" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} data-id="element-1406">
                <h2 className="text-2xl font-display mb-6 text-white" data-id="element-1407">
                  15. Contact
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed" data-id="element-1408">
                  <p data-id="element-1409">For questions regarding these Terms:</p>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-6 mt-6" data-id="element-1410">
                    <p className="mb-2" data-id="element-1411">
                      <span className="text-white font-medium" data-id="element-1412">Aijentik</span>
                    </p>
                    <p className="mb-2" data-id="element-1413">
                      <span className="text-gray-400" data-id="element-1414">Email:</span>{' '}
                      <a href="mailto:legal@aijentik.com" className="text-brand-orange hover:text-brand-deep-orange transition-colors" data-id="element-1415">
                        legal@aijentik.com
                      </a>
                    </p>
                    <p data-id="element-1416">
                      <span className="text-gray-400" data-id="element-1417">Website:</span>{' '}
                      <a href="https://aijentik.com" className="text-brand-orange hover:text-brand-deep-orange transition-colors" data-id="element-1418">
                        aijentik.com
                      </a>
                    </p>
                  </div>
                </div>
              </motion.section>
            </div>
          </div>

          {/* Sticky Sidebar */}
          <div className="hidden lg:block w-72 shrink-0" data-id="element-1419">
            <div className="sticky top-32" data-id="element-1420">
              <h4 className="text-xs font-bold tracking-widest text-gray-500 uppercase mb-6" data-id="element-1421">
                Contents
              </h4>
              <nav className="flex flex-col space-y-1 border-l border-white/10" data-id="element-1422">
                {sections.map(section => <button key={section.id} onClick={() => scrollToSection(section.id)} className={`text-left px-4 py-2 text-sm transition-all duration-200 border-l-[3px] -ml-[2px] ${activeSection === section.id ? 'border-brand-orange text-white font-medium bg-white/5' : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-white/20'}`} data-id="element-1423">
                    {section.title}
                  </button>)}
              </nav>

              <div className="mt-12 p-6 bg-brand-orange/10 border border-brand-orange/20 rounded-xl" data-id="element-1424">
                <p className="text-sm text-brand-orange/90 leading-relaxed" data-id="element-1425">
                  Questions about these terms? We're here to help.
                </p>
                <a href="mailto:legal@aijentik.com" className="inline-block mt-4 text-sm font-medium text-white hover:text-brand-orange transition-colors" data-id="element-1426">
                  Contact Legal Team →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>;
}